// Kernvel-staff API client (`/api/v1/staff`) — deliberately separate from `api.ts`.
//
// The staff token is a short-lived Ed25519 token minted OFFLINE by Kernvel
// (`scripts/kernvel_staff_issuer.py`); it is never an AdminUser session, never refreshed, and never
// sent to any other route. It lives in `sessionStorage` (this tab only) and is dropped the moment
// the backend refuses it or it expires.

const STAFF_BASE = "/api/v1/staff";
export const STAFF_TOKEN_KEY = "kernvel_staff_token";

export class StaffApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string) {
    super(code);
    this.status = status;
    this.code = code;
  }
}

export function getStaffToken(): string {
  try {
    return sessionStorage.getItem(STAFF_TOKEN_KEY) ?? "";
  } catch {
    return "";
  }
}

export function setStaffToken(token: string): void {
  try {
    if (token) sessionStorage.setItem(STAFF_TOKEN_KEY, token);
    else sessionStorage.removeItem(STAFF_TOKEN_KEY);
  } catch {
    /* storage unavailable: the token simply isn't remembered */
  }
}

function errorCode(body: unknown, fallback: string): string {
  const b = body as { detail?: unknown; errors?: { message?: unknown } } | null;
  const code = b?.errors?.message ?? b?.detail;
  return typeof code === "string" && code ? code : fallback;
}

export async function staffFetch<T = unknown>(path: string, init: RequestInit = {}, token = getStaffToken()): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (init.body !== undefined) headers.set("Content-Type", "application/json");
  const res = await fetch(`${STAFF_BASE}${path}`, { ...init, headers, credentials: "omit" });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401) setStaffToken("");
    throw new StaffApiError(res.status, errorCode(body, res.status === 404 ? "not_found" : `http_${res.status}`));
  }
  return ((body as { data?: T } | null)?.data ?? body) as T;
}

export const staffGet = <T = unknown>(path: string) => staffFetch<T>(path, { method: "GET" });
export const staffPost = <T = unknown>(path: string, body: unknown) =>
  staffFetch<T>(path, { method: "POST", body: JSON.stringify(body) });
export const staffPut = <T = unknown>(path: string, body: unknown) =>
  staffFetch<T>(path, { method: "PUT", body: JSON.stringify(body) });

/** A human sentence for the fixed backend codes the staff surface returns. */
export function staffErrorText(e: unknown, fallback: string): string {
  if (!(e instanceof StaffApiError)) return fallback;
  switch (e.code) {
    case "staff_surface_disabled":
    case "not_found":
      return e.status === 404 ? "Not found (or the staff surface is not configured on this deployment)." : e.code;
    case "staff_token_expired":
      return "Your staff token expired — mint a new one.";
    case "staff_token_revoked":
      return "This staff token was revoked — mint a new one.";
    case "staff_principal_inactive":
      return "This staff principal is not active.";
    case "staff_scope_missing":
      return "Your staff grant does not include this action.";
    default:
      return e.code;
  }
}
